import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { EduShell, EmptyState, SCHOOL_NAV, StatCard } from "@/components/EduShell";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useEducation } from "@/hooks/useEducation";
import { supabase } from "@/integrations/supabase/client";
import { downloadCsv, eduRpc } from "@/lib/education";

import { T } from "@/components/T";
import i18n from "@/i18n";
export const Route = createFileRoute("/school")({
  component: SchoolAdmin,
  head: () => ({
    meta: [
      { title: "School admin - Inquira" },
      {
        name: "description",
        content: "Invite teachers, manage classes and see how your school is using Inquira week to week.",
      },
      { property: "og:title", content: "Inquira school admin" },
      { property: "og:description", content: "Teacher invites, school directory and usage in one place." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

interface SchoolRow {
  id: string;
  name: string;
  school_code: string | null;
  school_type: string | null;
  city: string | null;
  state: string | null;
  teachers: number;
  students: number;
  classes: number;
}

interface Directory {
  members: Array<{
    user_id: string;
    role: string;
    status: string;
    display_name: string;
    classes: number;
  }>;
  invites: Array<{ id: string; email: string; role: string; status: string; created_at: string }>;
  classes: Array<{
    id: string;
    name: string;
    subject: string | null;
    grade_level: string | null;
    archived: boolean;
    teacher_name: string;
    students: number;
    assignments: number;
  }>;
}

interface Usage {
  since: string;
  assignments_created: number;
  submissions: number;
  graded: number;
  average_score: number | null;
  active_teachers: number;
  ai_by_kind: Record<string, number>;
  top_classes: Array<{ name: string; students: number; submissions: number }>;
}

function SchoolAdmin() {
  const { user, isLoading } = useAuth();
  const { isSchoolAdmin, teacherPortalEnabled, loading } = useEducation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [schools, setSchools] = useState<SchoolRow[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const [directory, setDirectory] = useState<Directory | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [invite, setInvite] = useState({ email: "", role: "teacher" });
  const [newSchool, setNewSchool] = useState({ name: "", code: "", city: "", state: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    let active = true;
    supabase
      .rpc("has_role" as never, { _user_id: user.id, _role: "super_admin" } as never)
      .then(({ data }) => { if (active) setIsAdmin(data === true); });
    return () => { active = false; };
  }, [user]);

  const loadSchools = useCallback(async () => {
    const { data } = await eduRpc<SchoolRow[]>("edu_my_schools");
    const list = data ?? [];
    setSchools(list);
    setActiveId((id) => id || (list[0]?.id ?? ""));
  }, []);

  const loadSchool = useCallback(async (schoolId: string) => {
    if (!schoolId) { setDirectory(null); setUsage(null); return; }
    const [dir, use] = await Promise.all([
      eduRpc<Directory>("edu_school_directory", { _school_id: schoolId }),
      eduRpc<Usage>("edu_school_usage", { _school_id: schoolId, _days: 30 }),
    ]);
    setDirectory(dir.data);
    setUsage(use.data);
  }, []);

  useEffect(() => {
    if (!user) return;
    void eduRpc("edu_accept_my_invites").then(() => loadSchools());
  }, [user, loadSchools]);

  useEffect(() => { void loadSchool(activeId); }, [activeId, loadSchool]);

  if (isLoading || loading) return null;

  if (!user) {
    return (
      <EduShell nav={SCHOOL_NAV} title={i18n.t("edu.school.school_admin")}>
        <EmptyState title={i18n.t("edu.school.sign_in_first")} />
        <Link to="/login" className="mt-4 inline-block rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
          Sign in
        </Link>
      </EduShell>
    );
  }

  if ((!teacherPortalEnabled || !isSchoolAdmin) && !isAdmin) {
    return (
      <EduShell nav={SCHOOL_NAV} title={i18n.t("edu.school.school_admin")} subtitle={i18n.t("edu.school.this_area_is_still_in_testing")}>
        <p className="text-sm text-ink-muted">
          School tools are not switched on for your account yet. Everything else in Inquira works as normal.
        </p>
        <Link to="/" className="mt-4 inline-block text-sm font-semibold text-neon-pink"><T k="edu.school.back_to_inquira" /></Link>
      </EduShell>
    );
  }

  const active = schools.find((s) => s.id === activeId) ?? null;

  async function createSchool() {
    if (!newSchool.name.trim() || !newSchool.code.trim()) {
      toast.error(i18n.t("edu.school.add_a_school_name_and_short_code"));
      return;
    }
    setBusy(true);
    const { data, error } = await eduRpc<string>("edu_create_school", {
      _name: newSchool.name,
      _school_code: newSchool.code,
      _city: newSchool.city || null,
      _state: newSchool.state || null,
    });
    setBusy(false);
    if (error) { toast.error(error); return; }
    toast.success(i18n.t("edu.school.school_created"));
    setNewSchool({ name: "", code: "", city: "", state: "" });
    await loadSchools();
    if (data) setActiveId(data);
  }

  async function sendInvite() {
    if (!activeId) return;
    setBusy(true);
    const { data, error } = await eduRpc<{ status: string }>("edu_school_invite", {
      _school_id: activeId,
      _email: invite.email,
      _role: invite.role,
    });
    setBusy(false);
    if (error) { toast.error(error); return; }
    toast.success(
      data?.status === "granted"
        ? "Access granted — they can open the teacher portal now"
        : "Invite saved. They get access as soon as they sign up with that email.",
    );
    setInvite({ email: "", role: "teacher" });
    await Promise.all([loadSchool(activeId), loadSchools()]);
  }

  async function cancelInvite(id: string) {
    const { error } = await eduRpc("edu_school_cancel_invite", { _invite_id: id });
    if (error) { toast.error(error); return; }
    await loadSchool(activeId);
  }

  async function setMember(userId: string, role: string, activeState: boolean) {
    const { error } = await eduRpc("edu_school_set_member_status", {
      _school_id: activeId,
      _user_id: userId,
      _role: role,
      _active: activeState,
    });
    if (error) { toast.error(error); return; }
    toast.success(activeState ? "Teaching access restored" : "Teaching access paused");
    await Promise.all([loadSchool(activeId), loadSchools()]);
  }

  return (
    <EduShell
      nav={SCHOOL_NAV}
      title={active ? active.name : "School admin"}
      subtitle={i18n.t("edu.school.invite_teachers_keep_an_eye_on_classes_a")}
      actions={
        directory && directory.classes.length > 0 ? (
          <Button
            variant="outline"
            onClick={() =>
              downloadCsv(
                `${active?.school_code ?? "school"}-classes.csv`,
                directory.classes.map((c) => ({
                  Class: c.name,
                  Teacher: c.teacher_name,
                  Subject: c.subject ?? "",
                  Students: c.students,
                  Assignments: c.assignments,
                })),
              )
            }
          >
            Export classes
          </Button>
        ) : undefined
      }
    >
      {schools.length > 1 && (
        <select
          className="mb-5 w-full rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink sm:w-72"
          value={activeId}
          onChange={(e) => setActiveId(e.target.value)}
        >
          {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      )}

      {schools.length === 0 ? (
        <div className="space-y-3 rounded-2xl border border-rule bg-card p-4">
          <p className="font-semibold text-ink"><T k="edu.school.add_your_school" /></p>
          <div className="grid gap-3 sm:grid-cols-2">
            <input className="rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
              placeholder={i18n.t("edu.school.school_name")} value={newSchool.name}
              onChange={(e) => setNewSchool({ ...newSchool, name: e.target.value })} />
            <input className="rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
              placeholder={i18n.t("edu.school.short_code_e_g_lincolnhs")} value={newSchool.code}
              onChange={(e) => setNewSchool({ ...newSchool, code: e.target.value })} />
            <input className="rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
              placeholder={i18n.t("edu.school.city")} value={newSchool.city}
              onChange={(e) => setNewSchool({ ...newSchool, city: e.target.value })} />
            <input className="rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
              placeholder={i18n.t("edu.school.state_region")} value={newSchool.state}
              onChange={(e) => setNewSchool({ ...newSchool, state: e.target.value })} />
          </div>
          <Button onClick={createSchool} disabled={busy}><T k="edu.school.create_school" /></Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <StatCard label={i18n.t("edu.school.teachers")} value={active?.teachers ?? "—"} />
            <StatCard label={i18n.t("edu.school.students")} value={active?.students ?? "—"} />
            <StatCard label={i18n.t("edu.school.classes")} value={active?.classes ?? "—"} />
            <StatCard label={i18n.t("edu.school.assignments")} value={usage?.assignments_created ?? "—"} hint="Last 30 days" />
            <StatCard label={i18n.t("edu.school.submissions")} value={usage?.submissions ?? "—"} hint="Last 30 days" />
            <StatCard label={i18n.t("edu.school.average_score")} value={usage?.average_score ?? "—"} />
          </div>

          <section className="mt-8 grid gap-6 lg:grid-cols-2">
            <div className="space-y-3 rounded-2xl border border-rule bg-card p-4">
              <p className="font-semibold text-ink"><T k="edu.school.invite_a_teacher" /></p>
              <div className="flex flex-wrap gap-2">
                <input
                  className="min-w-[12rem] flex-1 rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
                  placeholder={i18n.t("edu.school.teacher_school_edu")}
                  value={invite.email}
                  onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                />
                <select
                  className="rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
                  value={invite.role}
                  onChange={(e) => setInvite({ ...invite, role: e.target.value })}
                >
                  <option value="teacher"><T k="edu.school.teacher" /></option>
                  <option value="school_admin"><T k="edu.school.school_admin" /></option>
                </select>
                <Button onClick={sendInvite} disabled={busy || !invite.email.trim()}><T k="edu.school.invite" /></Button>
              </div>
              <p className="text-xs text-ink-muted">
                If they already have an Inquira account, access is granted straight away.
              </p>

              {(directory?.invites ?? []).length > 0 && (
                <ul className="mt-2 space-y-2 text-sm">
                  {directory!.invites.map((i) => (
                    <li key={i.id} className="flex items-center justify-between gap-2 rounded-xl border border-rule p-3">
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-ink">{i.email}</span>
                        <span className="text-xs text-ink-muted">{i.role.replace(/_/g, " ")} · {i.status}</span>
                      </span>
                      {i.status === "pending" && (
                        <button className="text-xs font-semibold text-destructive" onClick={() => cancelInvite(i.id)}>
                          Cancel
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <h2 className="mb-3 font-display text-lg font-bold text-ink">Staff &amp; students</h2>
              {(directory?.members ?? []).length === 0 ? (
                <EmptyState title={i18n.t("edu.school.nobody_here_yet")} body="Invite your first teacher to get started." />
              ) : (
                <ul className="space-y-2">
                  {directory!.members.map((m) => (
                    <li key={`${m.user_id}-${m.role}`} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-rule bg-card p-3">
                      <span>
                        <span className="block font-semibold text-ink">{m.display_name}</span>
                        <span className="text-xs text-ink-muted">
                          {m.role.replace(/_/g, " ")} · {m.status}
                          {m.role === "teacher" ? ` · ${m.classes} class${m.classes === 1 ? "" : "es"}` : ""}
                        </span>
                      </span>
                      {m.role === "teacher" && (
                        <Button
                          size="sm"
                          variant={m.status === "active" ? "outline" : "default"}
                          onClick={() => setMember(m.user_id, "teacher", m.status !== "active")}
                        >
                          {m.status === "active" ? "Pause access" : "Grant access"}
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <section className="mt-8 grid gap-6 lg:grid-cols-2">
            <div>
              <h2 className="mb-3 font-display text-lg font-bold text-ink"><T k="edu.school.classes" /></h2>
              {(directory?.classes ?? []).length === 0 ? (
                <EmptyState title={i18n.t("edu.school.no_classes_yet")} />
              ) : (
                <ul className="space-y-2">
                  {directory!.classes.map((c) => (
                    <li key={c.id} className="rounded-2xl border border-rule bg-card p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-semibold text-ink">{c.name}</span>
                        <span className="text-xs text-ink-muted">{c.students} students · {c.assignments} assignments</span>
                      </div>
                      <p className="text-sm text-ink-muted">{[c.teacher_name, c.subject].filter(Boolean).join(" · ")}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <h2 className="mb-3 font-display text-lg font-bold text-ink"><T k="edu.school.usage_last_30_days" /></h2>
              <div className="space-y-2 rounded-2xl border border-rule bg-card p-4 text-sm text-ink-muted">
                <p><span className="font-semibold text-ink">{usage?.active_teachers ?? 0}</span> <T k="edu.school.teachers_set_work" /></p>
                <p><span className="font-semibold text-ink">{usage?.graded ?? 0}</span> <T k="edu.school.submissions_graded" /></p>
                {Object.keys(usage?.ai_by_kind ?? {}).length > 0 && (
                  <div>
                    <p className="mt-2 font-semibold text-ink"><T k="edu.school.ai_help_used" /></p>
                    <ul className="mt-1 space-y-1">
                      {Object.entries(usage!.ai_by_kind).map(([kind, n]) => (
                        <li key={kind}>{kind.replace(/_/g, " ")}: {n}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {(usage?.top_classes ?? []).length > 0 && (
                  <div>
                    <p className="mt-2 font-semibold text-ink"><T k="edu.school.busiest_classes" /></p>
                    <ul className="mt-1 space-y-1">
                      {usage!.top_classes.map((c) => (
                        <li key={c.name}>{c.name}: {c.submissions} submissions</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </section>
        </>
      )}
    </EduShell>
  );
}
